import React, { useState, useEffect, useContext } from 'react'
import styles from '../../../styles/Register.module.scss'
import techTalentStyles from '../../../styles/TechTalent.module.scss'
import axios from 'axios'
import { useRouter } from 'next/router'
import { createClient } from 'graphqurl'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import {
	Form,
	Row,
	Col,
	Button,
	Alert,
	Spinner,
	InputGroup,
	FormControl
} from 'react-bootstrap'

import UserContext from '../../../UserContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEyeSlash, faLock } from '@fortawesome/free-solid-svg-icons'
import { faEye } from '@fortawesome/free-regular-svg-icons'
import ErrorDialog from '../ErrorDialog'

export default function AvailabilityStatus({ inputs, setInputs, confirmPw, setConfirmPw, boolean, setBoolean, imgData, cvData, schema, setSchema, isNewProfile }): JSX.Element {
	const { currentlyEmployed, startWorkImmediately, broadcastToEmployers } = boolean
	const { t } = useTranslation()
	const [showAlert, setShowAlert] = useState(false)
	const [showPass, setShowPass] = useState(false)
	const [showConfPass, setShowConfPass] = useState(false)

	// for loading "message"
	const [showLoading, setShowLoading] = useState(false)

	// for validation errors modal
	const [showValidationModal, setShowValidationModal] = useState(false)
	const [validationErrors, setValidationErrors] = useState<string[]>([])

	// for terms agreement
	const [agreeToTerms, setAgreeToTerms] = useState(isNewProfile === false)

	const { user, setUser } = useContext(UserContext)

	const messageMap: { [key: string]: string } = {
		'Name is required': 'Please enter your name',
		'Name must be at least 2 characters': 'Name must be at least 2 characters',
		'Email is required': 'Please enter your email address',
		'Please enter a valid email': 'Please enter a valid email address',
		'Gender is required': 'Please select your gender',
		'Invalid gender': 'Please select a valid gender',
		'dateOfBirth must be a `date` type, but the final value was: `Invalid Date` (cast from the value `""`).': 'Please enter your date of birth',
		'NRIC is required': 'Please enter your NRIC',
		'Please enter a valid NRIC': 'Please enter a valid NRIC',
		'Please select your nationality status': 'Please select your nationality status',
		'Education level is required': 'Please select your education level',
		'Unemployed duration is required': 'Please enter how long you have been unemployed',
		'broadcastStartDate must be a `date` type, but the final value was: `Invalid Date` (cast from the value `""`).': 'Please enter a valid broadcast start date',
		'broadcastEndDate must be a `date` type, but the final value was: `Invalid Date` (cast from the value `""`).': 'Please enter a valid broadcast end date',
		'broadcastStartDate must be a `date` type, but the final value was: `Invalid Date`.': 'Please enter a valid broadcast start date',
		'broadcastEndDate must be a `date` type, but the final value was: `Invalid Date`.': 'Please enter a valid broadcast end date',
		'Broadcast end date must be after start date': 'Broadcast end date must be after start date',
		'Broadcast to employers is required': 'Please select whether to broadcast to employers',
		'Resume is required': 'Please upload your resume',
		'Employment status is required': 'Please select your employment status',
		'Work availability is required': 'Please select your work availability',
		'Password is required': 'Please enter a password',
		'Password must be at least 8 characters': 'Password must be at least 8 characters',
		'Password must contain at least one uppercase letter, one lowercase letter, and one number': 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
		'Confirm password is required': 'Please confirm your password',
		'Passwords must match': 'Passwords must match'
	}

	const router = useRouter()
	const source = router.query.source
	const pathName = router.pathname

	useEffect(() => {
		const availabilityStatusSchema = Yup.object().shape({
			isEmployed: Yup.boolean().required('Employment status is required'),
			isStartWorkNow: Yup.boolean().required('Work availability is required'),
			unemployedDuration: Yup.string().when('isEmployed', {
				is: false,
				then: Yup.string().required('Unemployed duration is required')
			}),
			broadcastToEmployers: Yup.string().required('Broadcast to employers is required'),
			broadcastStartDate: Yup.date().nullable().test('broadcast-start-required', 'Broadcast start date is required', function (value) {
				if (this.parent.broadcastToEmployers === 'true') {
					return value != null && value instanceof Date && !isNaN(value.getTime())
				}
				return true
			}),
			broadcastEndDate: Yup.date().nullable().test('broadcast-end-required', 'Broadcast end date is required', function (value) {
				if (this.parent.broadcastToEmployers === 'true') {
					return value != null && value instanceof Date && !isNaN(value.getTime())
				}
				return true
			}).test('broadcast-end-after-start', 'Broadcast end date must be after start date', function (value) {
				if (this.parent.broadcastToEmployers === 'true' && this.parent.broadcastStartDate && value) {
					return value > this.parent.broadcastStartDate
				}
				return true
			}),
			password: Yup.string().when('user.email', {
				is: null,
				then: Yup.string()
					.required('Password is required')
					.min(8, 'Password must be at least 8 characters')
					.matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one uppercase letter, one lowercase letter, and one number')
			}),
			confirmPassword: Yup.string().when('user.email', {
				is: null,
				then: Yup.string()
					.required('Confirm password is required')
					.test('passwords-match', 'Passwords must match', function (value) {
						return this.parent.password === value
					})
			})
		})

		setSchema(prevSchema => prevSchema.concat(availabilityStatusSchema))
	}, [setSchema, currentlyEmployed, startWorkImmediately, broadcastToEmployers, isNewProfile])

	const handleInsert = async (event): Promise<void> => {
		event.preventDefault()
		event.stopPropagation()

		// Validate schema first
		try {
			await schema.validate({
				...inputs,
				...boolean,
				user,
				confirmPassword: confirmPw
			}, { abortEarly: false })
		} catch (validationError: any) {
			const fieldOrder = ['name', 'email', 'gender', 'dateOfBirth', 'nric', 'isLocale', 'locationMrt', 'locationCity', 'nationality', 'workPass', 'cvName', 'unemployedDuration', 'broadcastToEmployers', 'broadcastStartDate', 'broadcastEndDate']
			const errorsMap = {}
			validationError.inner.forEach((err: any) => {
				if (!errorsMap[err.path]) {
					errorsMap[err.path] = messageMap[err.message] || err.message
				}
			})
			const errors = Object.keys(errorsMap)
				.map(field => ({ field, message: errorsMap[field] }))
				.sort((a: any, b: any) => {
					const indexA = fieldOrder.indexOf(a.field)
					const indexB = fieldOrder.indexOf(b.field)
					return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB)
				})
				.map((err: any) => err.message)
			setValidationErrors(errors)
			setShowValidationModal(true)
			return
		}

		setShowLoading(true)

		const res: any = await axios.post(process.env.NEXT_PUBLIC_SIGNUP_ROUTE as string, {
			...inputs
		})

		const obj = res.data
		console.log('Signup response:', obj)

		if (obj.applicant !== undefined && obj.token !== undefined) {
			if (cvData !== undefined || imgData !== undefined) {
				const form = new window.FormData()
				form.append('id', obj.applicant.id)
				form.append('contact', inputs.contact)
				if (cvData !== undefined) {
					form.append('cv', cvData)
				}
				if (imgData !== undefined) {
					form.append('avatar', imgData)
				}
				await axios.post(process.env.NEXT_PUBLIC_UPLOAD_ENDPOINT as string, form, {
					headers: { 'Content-Type': 'multipart/form-data' }
				})
			}
			window.localStorage.setItem('token', obj.token)
			window.localStorage.setItem('profile', JSON.stringify(obj.applicant))
			setUser(obj.applicant)

			const redirectUrl = typeof router.query.redirect !== 'undefined' ? router.query.redirect as string : '/profile-completed'
			router.push(redirectUrl).catch(() => null)
		} else if (obj.isTaken === true) {
			setShowAlert(true)
			setShowLoading(false)
		} else {
			console.error('Unexpected response:', obj)
			setShowAlert(true)
			setShowLoading(false)
		}
	}

	const handleUpdate = async (event): Promise<void> => {
		event.preventDefault()
		event.stopPropagation()

		// Validate schema first
		try {
			await schema.validate({
				...inputs,
				...boolean,
				user,
				confirmPassword: confirmPw
			}, { abortEarly: false })
		} catch (validationError: any) {
			const fieldOrder = ['name', 'email', 'gender', 'dateOfBirth', 'nric', 'isLocale', 'locationMrt', 'locationCity', 'nationality', 'workPass', 'cvName', 'unemployedDuration', 'broadcastToEmployers', 'broadcastStartDate', 'broadcastEndDate']
			const errorsMap = {}
			validationError.inner.forEach((err: any) => {
				if (!errorsMap[err.path]) {
					errorsMap[err.path] = messageMap[err.message] || err.message
				}
			})
			const errors = Object.keys(errorsMap)
				.map(field => ({ field, message: errorsMap[field] }))
				.sort((a: any, b: any) => {
					const indexA = fieldOrder.indexOf(a.field)
					const indexB = fieldOrder.indexOf(b.field)
					return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB)
				})
				.map((err: any) => err.message)
			setValidationErrors(errors)
			setShowValidationModal(true)
			return
		}

		// if all required fields are filled
		const token = window.localStorage.getItem('token')

		const updatePassword = {
			password: inputs.password,
			id: user.id,
			token: token
		}

		if (cvData !== undefined || imgData !== undefined) {
			const form = new window.FormData()
			form.append('id', user.id)
			form.append('contact', user.contact)
			if (cvData !== undefined) {
				form.append('cv', cvData)
			}
			if (imgData !== undefined) {
				form.append('avatar', imgData)
			}
			const res: any = await axios.post(process.env.NEXT_PUBLIC_UPLOAD_ENDPOINT as string, form, {
				headers: { 'Content-Type': 'multipart/form-data' }
			})

			if (!(res.status === 200 && res.data === true)) {
				return
			}
		}

		if (inputs.cvName.includes('file selected') === true) {
			inputs.cvName = null
		}
		if (inputs.unemployedDuration === '') {
			inputs.unemployedDuration = null
		}
		if (inputs.locationMrt === null || inputs.locationMrt === '') {
			inputs.locationMrt = null
			inputs.locationOther = 'Not in Singapore'
		} else {
			const splitArr = inputs.locationMrt.split(' ')
			let stationName = ''
			for (let i = 1; i < splitArr.length; i++) {
				if (i === 1) stationName = stationName + splitArr[i]
				else stationName = stationName + ' ' + splitArr[i]
			}
			inputs.locationMrt = splitArr[0]
			inputs.locationOther = stationName
		}
		if (inputs.workPass === null || inputs.workPass === 'null' || inputs.workPass === '') {
			inputs.workPass = null
		}
		if (inputs.whatsapp === '') {
			inputs.whatsapp = null
		}
		inputs.age = moment().diff(moment(inputs.dateOfBirth, 'YYYYMMDD'), 'years')
		inputs.education = inputs.educationLevel !== 'OTHERS' ? inputs.educationLevel : inputs.education

		delete inputs.avatar
		delete inputs.cv
		delete inputs.password
		delete inputs.__typename

		try {
			const client = createClient({
				endpoint: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT as string,
				headers: {
					authorization: token ? `Bearer ${token}` : '',
				}
			})

			const hasuraMutation = client.query({
				query: `
					mutation ($id: Int!, $changes: Applicant_set_input) {
						update_Applicant_by_pk(pk_columns: {id: $id} _set: $changes) {
						id
						name
						email
						avatar
						contact
						}	
					}
				`,
				variables: {
					id: user.id,
					changes: inputs
				}
			})

			const hasuraRes = await hasuraMutation

			if (hasuraRes.data.update_Applicant_by_pk !== null) {
				window.localStorage.setItem('profile', JSON.stringify(hasuraRes.data.update_Applicant_by_pk))

				// Mutation succeed
				if (user.email !== null) {
					let redirectUrl = typeof router.query.redirect !== 'undefined' ? router.query.redirect as string : '/profile'
					if (redirectUrl === '/profile' && router.query.event) {
						redirectUrl = `/profile?event=${router.query.event}`
					}
					router.push(redirectUrl).catch(() => null)
				}
			}

			if (user.email === null) {
				axios.post(process.env.NEXT_PUBLIC_SIGNUP_UPDATE_PASSWORD as string, updatePassword).then(() => {
					const tempUser = user
					tempUser.email = inputs.email
					setUser(tempUser)
					const redirectUrl = typeof router.query.redirect !== 'undefined' ? router.query.redirect as string : (source === 'broadcast' ? '/broadcast-success' : '/browse-jobs')
					router.push(redirectUrl).catch(() => null)
				}).catch(() => {
					// do nothing
				})
			}
		} catch (err) {
		}
	}

	return (
		<Form onSubmit={user === null ? handleInsert : handleUpdate} noValidate>
			<Row className='mb-3'>
				<Col sm={12} md={6}>
					<Form.Group>
						<Form.Label>
							{t('update_profile.are_you_currently_employed')}
							<span className={styles.mandatory}>*</span>
						</Form.Label>
						<div>
							<Button
								variant={currentlyEmployed === 'true' ? 'danger' : 'light'}
								className={`${styles.selectItem} ${currentlyEmployed === 'true' && techTalentStyles.primaryBtn}`}
								onClick={() => {
									setBoolean({ ...boolean, currentlyEmployed: 'true' })
									setInputs({ ...inputs, isEmployed: true, unemployedDuration: '' })
								}}
							>
								{t('update_profile.yes')}
							</Button>
							<Button
								variant={currentlyEmployed === 'false' ? 'danger' : 'light'}
								className={`${styles.selectItem} ${currentlyEmployed === 'false' && techTalentStyles.primaryBtn}`}
								onClick={() => {
									setBoolean({ ...boolean, currentlyEmployed: 'false' })
									setInputs({ ...inputs, isEmployed: false })
								}}
							>
								{t('update_profile.no')}
							</Button>
						</div>
					</Form.Group>
				</Col>
				{currentlyEmployed === 'false' && (
					<Col sm={12} md={6}>
						<Form.Group>
							<Form.Label>
								{t('update_profile.how_long_have_you_been_unemployed')}
								<span className={styles.mandatory}>*</span>
							</Form.Label>
							<Form.Select
								as='select'
								value={inputs.unemployedDuration}
								onChange={event => {
									setInputs({
										...inputs,
										unemployedDuration: event.target.value
									})
								}}
							>
								<option value='' disabled>{t('update_profile.choose_an_option')}</option>
								<option value='<3m'>{t('update_profile.less_than3_months')}</option>
								<option value='3m6m'>{t('update_profile.3_6_months')}</option>
								<option value='>6m'>{t('update_profile.more_than_6')}</option>
							</Form.Select>
						</Form.Group>
					</Col>
				)}
			</Row>
			<Row className='mb-3'>
				<Col>
					<Form.Group>
						<Form.Label>
							{t('update_profile.are_you_able_to_start_work_immediately')}
							<span className={styles.mandatory}>*</span>
						</Form.Label>
						<div>
							<Button
								variant={startWorkImmediately === 'true' ? 'danger' : 'light'}
								className={`${styles.selectItem} ${startWorkImmediately === 'true' && techTalentStyles.primaryBtn}`}
								onClick={() => {
									setBoolean({ ...boolean, startWorkImmediately: 'true' })
									setInputs({ ...inputs, isStartWorkNow: true })
								}}
							>
								{t('update_profile.yes')}
							</Button>
							<Button
								variant={startWorkImmediately === 'false' ? 'danger' : 'light'}
								className={`${styles.selectItem} ${startWorkImmediately === 'false' && techTalentStyles.primaryBtn}`}
								onClick={() => {
									setBoolean({ ...boolean, startWorkImmediately: 'false' })
									setInputs({ ...inputs, isStartWorkNow: false })
								}}
							>
								{t('update_profile.no')}
							</Button>
						</div>
					</Form.Group>
				</Col>
			</Row>

			{(source !== 'broadcast' && pathName !== '/update-profile') && (
				<Row className='mb-3'>
					<Col>
						<Form.Group>
							<Form.Label>
								{t('update_profile.broadcast_to_employers')}
								<span className={styles.mandatory}>*</span>
							</Form.Label>
							<div>
								<Button
									variant={broadcastToEmployers === 'true' ? 'danger' : 'light'}
									className={`${styles.selectItem} ${broadcastToEmployers === 'true' && techTalentStyles.primaryBtn}`}
									onClick={() => {
										setBoolean({ ...boolean, broadcastToEmployers: 'true' })
										setInputs({
											...inputs,
											broadcastStartDate: moment().format(),
											broadcastEndDate: moment().add(1, 'years').format()
										})
									}}
								>
									{t('update_profile.yes')}
								</Button>
								<Button
									variant={broadcastToEmployers === 'false' ? 'danger' : 'light'}
									className={`${styles.selectItem} ${broadcastToEmployers === 'false' && techTalentStyles.primaryBtn}`}
									onClick={() => {
										setBoolean({ ...boolean, broadcastToEmployers: 'false' })
										setInputs({
											...inputs,
											broadcastStartDate: null,
											broadcastEndDate: null
										})
									}}
								>
									{t('update_profile.no')}
								</Button>
							</div>
						</Form.Group>
					</Col>
				</Row>
			)}

			<hr className='mb-5' />
			{(user === null || user.email === null) && (
				<>
					<h3 className='text-center mb-3'>{t('update_profile.create_password')}</h3>
					<Row>
						<Col>
							<Form.Group>
								<Form.Label>
									{t('update_profile.email')}
									<span className={styles.mandatory}>*</span>
								</Form.Label>
								<Form.Control
									className={`${styles.formControl} ${techTalentStyles.input}`}
									type='email'
									value={inputs.email}
									onChange={event => {
										setInputs({
											...inputs,
											email: event.target.value
										})
									}}
								/>
							</Form.Group>
						</Col>
					</Row>
					{showAlert && (
						<Alert
							variant='danger'
							onClose={() => setShowAlert(false)}
							dismissible
						>
							{t('update_profile.email_taken')}
						</Alert>
					)}
					<Row className='mb-3 mt-3'>
						<Col sm={12} md={6} className='mb-3'>
							<InputGroup>
								<InputGroup.Text style={{ borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px', backgroundColor: 'white', borderRight: '0px', borderColor: '#EBEBEB' }}>
									<FontAwesomeIcon icon={faLock} />
								</InputGroup.Text>
								<FormControl
									className={techTalentStyles.input}
									placeholder={t('login.password')}
									style={{ borderLeft: '0px', borderColor: '#EBEBEB' }}
									type={showPass ? 'text' : 'password'}
									value={inputs.password}
									onChange={event => {
										setInputs({
											...inputs,
											password: event.target.value
										})
									}}
								/>

								<InputGroup.Text style={{ backgroundColor: 'white', borderBottomRightRadius: '10px', borderLeft: '0px', borderTopRightRadius: '10px', borderColor: '#EBEBEB' }}>
									{showPass ? <FontAwesomeIcon icon={faEyeSlash} onClick={() => setShowPass(false)} /> : <FontAwesomeIcon icon={faEye} onClick={() => setShowPass(true)} />}
								</InputGroup.Text>

							</InputGroup>
						</Col>
						<Col>
							<InputGroup>
								<InputGroup.Text style={{ borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px', backgroundColor: 'white', borderRight: '0px', borderColor: '#EBEBEB' }}>
									<FontAwesomeIcon icon={faLock} />
								</InputGroup.Text>
								<FormControl
									className={techTalentStyles.input}
									placeholder={t('update_profile.confirm_password')}
									style={{ borderLeft: '0px', borderColor: '#EBEBEB' }}
									type={showConfPass ? 'text' : 'password'}
									value={confirmPw}
									onChange={event => setConfirmPw(event.target.value)}
								/>

								<InputGroup.Text style={{ backgroundColor: 'white', borderBottomRightRadius: '10px', borderLeft: '0px', borderTopRightRadius: '10px', borderColor: '#EBEBEB' }}>
									{showConfPass ? <FontAwesomeIcon icon={faEyeSlash} onClick={() => setShowConfPass(false)} /> : <FontAwesomeIcon icon={faEye} onClick={() => setShowConfPass(true)} />}
								</InputGroup.Text>
							</InputGroup>
						</Col>
					</Row>
					<Form.Check type='checkbox'>
						<Form.Check.Input
							type='checkbox'
							checked={agreeToTerms}
							onChange={(e) => setAgreeToTerms(e.target.checked)}
							className={techTalentStyles.checkbox}
						/>
						<Form.Check.Label>
							{t('update_profile.by_clicking_register')}&nbsp;
							<a target='_blank' href='/privacy-policy' className={techTalentStyles.link}>
								{t('update_profile.privacy_policy')}
							</a>&nbsp;
							{t('update_profile.and')}&nbsp;
							<a target='_blank' href='/terms-of-use' className={techTalentStyles.link}>
								{t('update_profile.terms_of_use')}
							</a>
						</Form.Check.Label>
					</Form.Check>
					<hr className='mt-4 mb-4' />
				</>
			)}

			<Row className='mt-3'>
				<Col className='pull-right'>
					<Button
						type='submit'
						className={`text-white ${techTalentStyles.primaryBtn}`}
						disabled={!agreeToTerms}
					>
						{t('update_profile.complete')}
					</Button>
				</Col>
			</Row>

			<Alert
				variant='dark'
				show={showLoading}
				className='text-center'
				style={{ width: '19em', position: 'fixed', top: '5%', left: '50%', marginTop: '-13px', marginLeft: '-10em' }}
			>
				<Spinner animation='border' variant='primary' />
				<h5 style={{ display: 'inline', marginLeft: '1em' }}>{t('update_profile.creating_your_profile')}</h5>
			</Alert>

			<ErrorDialog
				show={showValidationModal}
				onHide={() => setShowValidationModal(false)}
				title="Error"
				errors={validationErrors}
			/>
		</Form>
	)
}
